import React, { useEffect } from 'react'
import { useSnackbar } from 'Context/SnackbarContext/SnackbarContext'
import { GoalCategory } from 'Generated/graphql-types'
import { Resource } from 'Generated/academy-graphql-types'
import { ActionElementType } from 'Components/SharedComponents/Surveys/constants'
import {
  Description,
  StepContainer,
  Title,
} from 'Components/SharedComponents/Surveys/Surveys.styled'
import { NextActions } from 'Components/SharedComponents/Surveys/NextActions/NextActions'
import {
  ButtonAction,
  Priorities,
} from 'Components/SharedComponents/Surveys/types'
import { NeedsAssessmentWizardStep } from '../../constants'
import { HandleAnyAction, HandleToggleOrSaveResource } from '../../types'
import { RecommendedResources } from '../RecommendedResources/RecommendedResources'

type ThankYouForSharingProps = {
  onAnyAction: HandleAnyAction
  onBookmarkResources: HandleToggleOrSaveResource
  onFetchRecommendedResources: () => void
  priorities: Priorities
  recommendedResources?: Resource[]
  resourcesToBookmark: string[]
}

export const ThankYouForSharing = ({
  onAnyAction,
  onBookmarkResources,
  onFetchRecommendedResources,
  priorities,
  recommendedResources,
  resourcesToBookmark,
}: ThankYouForSharingProps) => {
  const { popToast } = useSnackbar()
  const toastMessage = 'Your resources were successfully bookmarked!'
  const REDIRECT_TIMEOUT_IN_MS = 5000

  useEffect(() => {
    if (recommendedResources && !recommendedResources?.length) {
      onFetchRecommendedResources()
    }
  }, [onFetchRecommendedResources, recommendedResources])

  const handleBookmarkAndRedirect = async () => {
    await onBookmarkResources('save')
    popToast({
      content: `${toastMessage} Redirecting in ${
        REDIRECT_TIMEOUT_IN_MS / 1000
      } seconds...`,
    })
    setTimeout(() => {
      window.location.href = '/academy/saved-resources'
    }, REDIRECT_TIMEOUT_IN_MS)
  }

  const isBookmarkButtonDisabled = resourcesToBookmark.length === 0
  const isSomethingElseFlow =
    priorities.firstPriority.category === GoalCategory.None
  const path = isSomethingElseFlow
    ? NeedsAssessmentWizardStep.ADD_YOUR_MISSION_STATEMENT
    : undefined

  const nextActions: ButtonAction[] = [
    {
      callback: isSomethingElseFlow
        ? undefined
        : () => {
            window.location.href = '/home'
          },
      label: recommendedResources ? 'Skip' : 'Explore Resilia',
      type: ActionElementType.BUTTON,
      variant: 'secondary',
      path,
    },
    {
      callback: isSomethingElseFlow
        ? () => {
            onBookmarkResources('save')
            popToast({
              content: toastMessage,
            })
          }
        : handleBookmarkAndRedirect,
      disabled: isBookmarkButtonDisabled,
      isHidden: !recommendedResources,
      label: 'Bookmark Selection',
      type: ActionElementType.BUTTON,
      path,
    },
  ]

  return (
    <StepContainer>
      <Title>Thank you for sharing!</Title>
      {recommendedResources && (
        <>
          <Description>
            Here {recommendedResources.length > 1 ? 'are a few' : 'is a '}{' '}
            recommended Academy resource{recommendedResources.length > 1 && 's'}{' '}
            based on your responses.
          </Description>
          <RecommendedResources
            onBookmark={onBookmarkResources}
            priorities={priorities}
            resources={recommendedResources}
            resourcesToBookmark={resourcesToBookmark}
          />
        </>
      )}
      <NextActions nextActions={nextActions} onAnyAction={onAnyAction} />
    </StepContainer>
  )
}
